import type P4ValidationRepository from "@/core/compliance/repositories/P4ValidationRepository";
import {
  P4ValidationStatus,
  RiskClassification,
  ScoringTrigger,
  type P4PendingCount,
  type P4ValidationItem,
} from "@/core/compliance/entities/P4Validation";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";

const MOCK_VALIDATIONS: P4ValidationItem[] = [
  {
    validationId: "val-001",
    businessKey: "CUST-00012345",
    oldRiskClass: RiskClassification.LOW,
    newRiskClass: RiskClassification.HIGH,
    oldScore: 22.4,
    newScore: 81.7,
    scoreDelta: 59.3,
    triggerReason: "PEP_HIT",
    sourceTrigger: ScoringTrigger.SCREENING_HIT,
    status: P4ValidationStatus.PENDING,
    slaDeadline: new Date(Date.now() + 10 * 60 * 60 * 1000).toISOString(),
    slaExpired: false,
    flaggedForDirectionRisques: false,
    createdAt: new Date(Date.now() - 38 * 60 * 60 * 1000).toISOString(),
  },
  {
    validationId: "val-002",
    businessKey: "CUST-00098765",
    oldRiskClass: RiskClassification.HIGH,
    newRiskClass: RiskClassification.LOW,
    oldScore: 85.2,
    newScore: 18.6,
    scoreDelta: -66.6,
    triggerReason: "MANUAL",
    sourceTrigger: ScoringTrigger.MANUAL,
    status: P4ValidationStatus.PENDING,
    slaDeadline: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    slaExpired: true,
    flaggedForDirectionRisques: true,
    createdAt: new Date(Date.now() - 52 * 60 * 60 * 1000).toISOString(),
  },
  {
    validationId: "val-003",
    businessKey: "CUST-00054321",
    oldRiskClass: RiskClassification.STANDARD,
    newRiskClass: RiskClassification.HIGH,
    oldScore: 48.0,
    newScore: 77.5,
    scoreDelta: 29.5,
    triggerReason: "SANCTIONS_HIT",
    sourceTrigger: ScoringTrigger.ONBOARDING,
    status: P4ValidationStatus.PENDING,
    slaDeadline: new Date(Date.now() + 20 * 60 * 60 * 1000).toISOString(),
    slaExpired: false,
    flaggedForDirectionRisques: false,
    createdAt: new Date(Date.now() - 28 * 60 * 60 * 1000).toISOString(),
  },
  {
    validationId: "val-004",
    businessKey: "CUST-00011223",
    oldRiskClass: RiskClassification.LOW,
    newRiskClass: RiskClassification.HIGH,
    oldScore: 20.1,
    newScore: 79.4,
    scoreDelta: 59.3,
    triggerReason: "RECLASSIFICATION",
    sourceTrigger: ScoringTrigger.ICR_CHANGE,
    status: P4ValidationStatus.APPROVED,
    slaDeadline: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    slaExpired: false,
    flaggedForDirectionRisques: false,
    createdAt: new Date(Date.now() - 60 * 60 * 60 * 1000).toISOString(),
  },
  {
    validationId: "val-005",
    businessKey: "CUST-00033445",
    oldRiskClass: RiskClassification.LOW,
    newRiskClass: RiskClassification.HIGH,
    oldScore: 24.8,
    newScore: 82.0,
    scoreDelta: 57.2,
    triggerReason: "RECLASSIFICATION",
    sourceTrigger: ScoringTrigger.ICR_CHANGE,
    status: P4ValidationStatus.REJECTED,
    slaDeadline: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    slaExpired: false,
    flaggedForDirectionRisques: true,
    createdAt: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
  },
];

export default class FakeP4ValidationRepositoryBackend implements P4ValidationRepository {
  private validations: P4ValidationItem[] = [...MOCK_VALIDATIONS];

  async getPendingValidations(): Promise<RepositoryResult<P4ValidationItem[]>> {
    await new Promise((resolve) => setTimeout(resolve, 600));
    const pending = this.validations.filter((v) => v.status === P4ValidationStatus.PENDING);
    return RepositoryResultFactory.success(pending);
  }

  async getValidationsByStatus(
    status: P4ValidationStatus,
  ): Promise<RepositoryResult<P4ValidationItem[]>> {
    await new Promise((resolve) => setTimeout(resolve, 600));
    const items = this.validations.filter((v) => v.status === status);
    return RepositoryResultFactory.success(items);
  }

  async getPendingCount(): Promise<RepositoryResult<P4PendingCount>> {
    await new Promise((resolve) => setTimeout(resolve, 200));
    const count = this.validations.filter((v) => v.status === P4ValidationStatus.PENDING).length;
    return RepositoryResultFactory.success({ count });
  }

  async approveValidation(validationId: string, _notes?: string): Promise<RepositoryResult<void>> {
    await new Promise((resolve) => setTimeout(resolve, 400));
    this.validations = this.validations.map((v) =>
      v.validationId === validationId ? { ...v, status: P4ValidationStatus.APPROVED } : v,
    );
    return RepositoryResultFactory.success(undefined);
  }

  async rejectValidation(validationId: string, _rejectionReason: string): Promise<RepositoryResult<void>> {
    await new Promise((resolve) => setTimeout(resolve, 400));
    this.validations = this.validations.map((v) =>
      v.validationId === validationId ? { ...v, status: P4ValidationStatus.REJECTED } : v,
    );
    return RepositoryResultFactory.success(undefined);
  }
}
